// THE ENTRY POINT — and the one place that chooses which transport the app runs on.
//
// It chooses the real transport only when two things are true, and SAYS WHY OUT LOUD when it cannot:
//   • a base URL (`VITE_V2_BASE_URL`) — `resolveHttpConfig` returns null without one, and there is deliberately no
//     fallback origin: a client that can reach a real project by forgetting a variable is a client that will;
//   • the two token suppliers (`startFirebase`) — which refuse, naming the build variables that are missing.
// `web/.env.example` is the list, with nothing behind any of it, and `docs/WEB_READINESS.md` says who supplies what.
//
// WHAT HAPPENS WHEN IT CANNOT DEPENDS ON THE BUILD (B8, LAUNCH-COPY-B8):
//   • every build but production's — `npm run dev`, a test, `--mode staging` with its values missing — runs on the FAKE,
//     with the reason in the console, exactly as before B8;
//   • THE PRODUCTION BUILD (app.proppeal.com, `npm run build`) NEVER DOES. The fake is not in its bundle: everything fake
//     is in `transport/fake/boot.ts`, reached by one dynamic import inside `if (import.meta.env.MODE !== PRODUCTION_MODE)`,
//     a branch Vite turns into a constant and Rollup drops (`test/productionBuild.test.ts` builds both and greps the
//     output). Instead it renders `StartupRefusal` — the token refusal Tyler approved for this moment. And it rarely gets
//     here: `web/deployTargets.ts` refuses to BUILD production without every production value, so what remains at run time
//     is Firebase failing to start in the browser.
// Until B8 this file built the fake on every load, whatever the build, and then chose; a production bundle carried it.
//
// WHAT CHANGED IN W4.2-local: the SDK half exists (`services/firebaseSdk.ts`), so a `.env.local` pointing at the LOCAL
// EMULATORS makes this function build the real transport and run the app against the real v2 backend.
//
// NO APPEAL ID IS HANDED IN (WEB-APPEAL-ID). The session starts with whatever this tab kept (`app/appealId.ts`) or none,
// and adopts the one the server mints. Until then this file handed it the fake world's fixed sample id even when it had
// chosen the real transport, and the real server refused it NOT_FOUND: the fake world is the fake's, and reaches nothing
// but the fake transport and the fake checkout — all of them built together in `transport/fake/boot.ts`.

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { PRODUCTION_MODE } from "./app/buildModes";
import { SessionProvider } from "./app/session";
import {
  createBrowserCheckout, createFirebaseAuthSession, createTransportAccountEmails, type AppCheckTokens, type AuthSession,
  type Checkout,
} from "./services";
import { describeFirebaseRefusal, startFirebase, type FirebaseAccount, type FirebaseTokens } from "./services/firebase";
import { createBrowserSuggester } from "./services/addressSuggest";
import { createBrowserSatellite } from "./services/satellite";
import { BASE_URL_VAR, createHttpTransport, readBaseUrl, resolveHttpConfig, type HttpConfig } from "./transport/http";
import type { Transport } from "./transport/types";
import "./styles/app.css";
import "./styles/depth.css";
import { DevPreview } from "./components/DevPreview";
import { StartupRefusal } from "./components/StartupRefusal";

const env = import.meta.env as unknown as Record<string, string | undefined>;
/** The build variables the fake reads (ACC-3a; LAUNCH-PF; R-pending-web-free-first). Named once, here; `.env.example`
 *  lists them. */
const FAKE_WEB_ACCOUNTS_VAR = "VITE_FAKE_WEB_ACCOUNTS";
const FAKE_PURCHASE_NOTICE_VAR = "VITE_FAKE_PURCHASE_NOTICE";
const FAKE_FREE_CHECK_VAR = "VITE_FAKE_FREE_CHECK";

/** What the page runs on, once started. */
interface Started {
  readonly transport: Transport;
  readonly auth: AuthSession;
  readonly checkout: Checkout;
  readonly appCheck: AppCheckTokens;
  readonly onFake: boolean;
}
type Start = { readonly kind: "started"; readonly started: Started } | { readonly kind: "refused"; readonly problem: string };

/**
 * NO REAL SERVER FOR THIS BUILD: the fake, with the reason said out loud — or, in the production build, a refusal.
 *
 * THE CONDITION IS WRITTEN OUT, NOT CALLED. `import.meta.env.MODE` becomes a string literal in a built bundle, so in
 * production this `if` is `"production" !== "production"` and the bundler removes the branch — and with it the only import
 * of the fake. A helper function answering the same question would keep the import alive.
 */
async function withoutServer(problem: string, say: (line: string) => void): Promise<Start> {
  if (import.meta.env.MODE !== PRODUCTION_MODE) {
    say(`PROppeal: ${problem} Running on the fake.`);
    const { bootFake } = await import("./transport/fake/boot");
    const fake = bootFake({
      webAccounts: env[FAKE_WEB_ACCOUNTS_VAR] === "true",
      purchaseNotice: env[FAKE_PURCHASE_NOTICE_VAR] === "true",
      freeCheck: env[FAKE_FREE_CHECK_VAR] === "true",
    });
    return { kind: "started", started: { ...fake, onFake: true } };
  }
  console.error(`PROppeal: ${problem} This is the production build, which never runs on the fake.`);
  return { kind: "refused", problem };
}

/** THE REAL SESSION GOES WITH THE REAL TRANSPORT (WEB-PAY-2, F-WP-2). The account the SDK started is the session's, so
 *  `/delete-account` ends it after the purge. ACC-3a: the account emails are the server's (ACC-1's four v2Credits ops, over
 *  this transport), never Firebase's own; the server answers them only with its WEB_ACCOUNTS_ENABLED on. */
function realSession(config: HttpConfig, firebase: { account: FirebaseAccount; tokens: FirebaseTokens }) {
  const transport = createHttpTransport(config);
  const auth = createFirebaseAuthSession(firebase.account, firebase.tokens, createTransportAccountEmails(transport));
  return { transport, auth };
}

/**
 * The real transport, or `withoutServer` with the reason.
 *
 * A MALFORMED BASE URL IS ITS OWN MESSAGE. Someone who sets `VITE_V2_BASE_URL=/api` meant to point this build
 * somewhere; treating that as "no variable" and starting quietly is how a staging build ends up looking like it works
 * while talking to nobody.
 */
async function start(): Promise<Start> {
  const base = readBaseUrl(env[BASE_URL_VAR]);
  if (base.kind === "malformed") return withoutServer(`${base.problem}.`, (line) => console.error(line));
  if (base.kind === "absent") {
    return withoutServer(`no ${BASE_URL_VAR} is set, so this build has no server. See web/.env.example.`, (line) => console.info(line));
  }

  // THE FIREBASE SDK IS LOADED HERE AND NOWHERE EARLIER (W4.2-local).
  //
  // `services/firebaseSdk.ts` is the only file in this package that imports `firebase`, and this `await import` is
  // the only thing that reaches it. It sits AFTER the base-URL check on purpose: a build with no `VITE_V2_BASE_URL`
  // never loads a byte of the SDK, so W4.1's rule survives adding the dependency ("a build with no config must refuse
  // to call a server rather than quietly reach one"). `vite build` puts it in its own chunk.
  const { createFirebaseWebSdk } = await import("./services/firebaseSdk");
  const firebase = await startFirebase(env, createFirebaseWebSdk(env));
  if (!firebase.ok) return withoutServer(describeFirebaseRefusal(firebase.reason), (line) => console.warn(line));

  const config = resolveHttpConfig(env, firebase.tokens);
  if (config === null) {
    // Unreachable: `base` was reachable above. Named rather than assumed, because an `if` that cannot fire is the
    // one that fires.
    return withoutServer(`${BASE_URL_VAR} resolved to no config after all.`, (line) => console.error(line));
  }
  const { transport, auth } = realSession(config, firebase);
  return {
    kind: "started",
    started: {
      transport,
      auth,
      // Against a real server the browser leaves for Stripe's hosted page (WEB-PAY-1; test mode unless the server's held
      // live-payments switch is on, LAUNCH-PAY).
      checkout: createBrowserCheckout(),
      // The session's App Check reader is the SDK's own, the one the transport's calls already carry.
      appCheck: { getToken: () => firebase.tokens.getAppCheckToken() },
      onFake: false,
    },
  };
}

const begun = await start();
const root = createRoot(document.getElementById("root") as HTMLElement);

if (begun.kind === "refused") {
  root.render(
    <StrictMode>
      <StartupRefusal problem={begun.problem} />
    </StrictMode>,
  );
} else {
  const { transport, auth, checkout, appCheck, onFake } = begun.started;
  // Apple's address suggestions (WEB-SUGGEST): one per page load. On a build with no token function behind it — `npm run
  // dev`, or a project with no MapKit key — the first answer says so and the box stays plain.
  const addressSuggester = createBrowserSuggester();
  const satelliteView = createBrowserSatellite();

  root.render(
    <StrictMode>
      <BrowserRouter>
        {/* WEB-DESIGN-1: the draft switch, for Tyler to read the one draft left in place on a dev build only
            (`components/DevPreview.tsx`). A production build drops it: `import.meta.env.DEV` is `false` there. */}
        {import.meta.env.DEV && <DevPreview />}
        <SessionProvider
          transport={transport}
          checkout={checkout}
          auth={auth}
          appCheck={appCheck}
          onFake={onFake}
          addressSuggester={addressSuggester}
          satelliteView={satelliteView}
        >
          <App />
        </SessionProvider>
      </BrowserRouter>
    </StrictMode>,
  );
}
